import { BarChart2, ChevronDown, ChevronUp, Clock, Search, Settings, Tag, XCircle } from 'react-feather';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import difference from 'lodash/difference';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import map from 'lodash/map';
import styled, { css } from 'styled-components';

import {
  AssignmentStatus,
  Gender,
  GetPurchaseStatsQuery as GetPurchaseStats,
  GetRegistrationFiltersQuery as GetRegistrationFilters,
  Locale,
  ParticipantFieldScope,
  Permission,
  PurchasePaymentStatus,
  PurchaseStatus,
} from '__generated__/graphql';
import config from '../../../config';

import { encodeUrl, isEmpty, toggleInList } from '../../../common/helpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetPurchaseStatsQuery from '../GetPurchaseStatsQuery';
import GetRegistrationFiltersQuery from './GetRegistrationFiltersQuery';
import ParticipantFieldFiltersForm, {
  ParticipantFieldFilters,
} from '../ParticipantFieldFiltersForm';
import ProtectedSection from '../../Common/ProtectedSection';
import SellableLabel from '../../../common/Common/SellableLabel';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

type Event = GetRegistrationFilters['project']['events']['data'][0];

export type PurchaseStatsData = GetPurchaseStats['project']['purchase_stats'];

export interface ParticipantFieldFilter {
  id: string;
  filters?: ParticipantFieldFilters;
}

export interface RegistrationFilterParams {
  event?: string | null;
  search?: string | null;
  min_age?: number | null;
  max_age?: number | null;
  locales?: Locale[];
  genders?: Gender[];
  mail_opt_ins?: string[];
  promotions?: string[];
  ticket_categories?: string[];
  tickets?: string[];
  time_slots?: string[];
  products?: string[];
  product_variants?: string[];
  statuses?: PurchaseStatus[];
  assignment_statuses?: AssignmentStatus[];
  payment_statuses?: PurchasePaymentStatus[];
  participant_fields?: ParticipantFieldFilter[];
  participant_field_choices?: string[];
  ticket_number_sequences?: string[];
  has_ticket_number?: boolean | null,
  has_team?: boolean | null,
  invitations?: string[];
  coupons?: string[];
  lotteries?: string[];
  confirmed_lotteries?: string[];
  rejected_lotteries?: string[];
}

export const defaultFilters: RegistrationFilterParams = {
  event: null,
  search: null,
  min_age: null,
  max_age: null,
  locales: [],
  genders: [],
  mail_opt_ins: [],
  promotions: [],
  ticket_categories: [],
  tickets: [],
  time_slots: [],
  products: [],
  product_variants: [],
  statuses: [],
  assignment_statuses: [],
  payment_statuses: [],
  participant_fields: [],
  participant_field_choices: [],
  ticket_number_sequences: [],
  has_ticket_number: null,
  has_team: null,
  invitations: [],
  coupons: [],
  lotteries: [],
  confirmed_lotteries: [],
  rejected_lotteries: [],
};

/**
 * Returns the participant fields that can be filtered on for the active event, tickets and products
 */
const getParticipantFields = (event: Event, ticketIds: string[], productIds: string[]) => {
  const tickets = event.ticket_categories.reduce((tickets, ticketCategory) => [...tickets, ticketCategory.tickets], []);

  return event.participant_fields.filter((field) => {
    if (field.scope !== ParticipantFieldScope.purchase) {
      return false;
    }

    const requiredTicketIds = tickets.filter(({ id }) => (
      field.tickets.map(({ id }) => id).includes(id)
    ));

    const requiredProductIds = event.products.filter(({ id }) => (
      field.products.map(({ id }) => id).includes(id)
    ));

    return (requiredTicketIds.length === 0
          || intersection(ticketIds, field.tickets.map(({ id }) => id)).length > 0)
        && (requiredProductIds.length === 0
          || intersection(productIds, field.products.map(({ id }) => id)).length > 0);
  });
};

type RegistrationFilterType = (
  'event' | 'search' | 'age' | 'gender' | 'mail_opt_ins' | 'tickets' | 'products' | 'team'
  | 'statuses' | 'assignment_statuses' | 'payment_statuses' | 'participant_fields'
  | 'ticket_number_sequences' | 'invitations' | 'coupons' | 'lotteries' | 'confirmed_lotteries'
  | 'rejected_lotteries' | 'locales'
);

export interface RegistrationFiltersProps {
  types?: RegistrationFilterType[];
  filters: RegistrationFilterParams;
  onChange: (update: (filters: RegistrationFilterParams) => RegistrationFilterParams) => void;
  onStatsChange?: (stats: PurchaseStatsData) => void;
  showTotals?: boolean;
  showResetButton?: boolean;
  showViewParticipantsButton?: boolean;
  readOnly?: boolean;
}

const RegistrationFilters = ({
  types, filters: incomingFilters, onChange, onStatsChange, showTotals = true, showResetButton = true,
  showViewParticipantsButton = false, readOnly = false,
}: RegistrationFiltersProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const enabled = (type: RegistrationFilterType) => !types || types.includes(type);

  const filters = {
    ...defaultFilters,
    ...incomingFilters,
  };

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the filters (tickets etc.)
  const { data: filtersData } = useQuery(
    GetRegistrationFiltersQuery,
    {
      variables: {
        events: filters.event ? [filters.event] : [],
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const mailPermissions = filtersData?.project.mail_permissions || [];
  const events = filtersData?.project.events.data || [];
  const event = events.filter(({ id }) => filters.event === id)[0] || null;
  const products = event?.products.filter((product) => !product.stand_alone) || [];

  // Fetches the stats
  const { data: statsData, loading: loadingStats } = useQuery(
    GetPurchaseStatsQuery,
    {
      variables: {
        events: filters.event ? [filters.event] : [],
        search: filters.search,
        min_age: filters.min_age,
        max_age: filters.max_age,
        genders: filters.genders,
        tickets: filters.tickets,
        ticket_categories: filters.ticket_categories,
        time_slots: filters.time_slots,
        products: filters.products,
        product_variants: filters.product_variants,
        promotions: filters.promotions,
        invitations: filters.invitations,
        coupons: filters.coupons,
        lotteries: filters.lotteries,
        confirmed_lotteries: filters.confirmed_lotteries,
        rejected_lotteries: filters.rejected_lotteries,
        statuses: filters.statuses,
        assignment_statuses: filters.assignment_statuses,
        payment_statuses: filters.payment_statuses,
        participant_fields: filters.participant_fields,
        participant_field_choices: filters.participant_field_choices,
        ticket_number_sequences: filters.ticket_number_sequences,
        has_ticket_number: filters.has_ticket_number,
        has_team: filters.has_team,
        participant_filters: {
          locales: filters.locales,
          mail_opt_ins: filters.mail_opt_ins,
        },
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        onStatsChange?.(data.project.purchase_stats);
      },
    },
  );

  const stats = statsData?.project.purchase_stats;

  // Participant fields grouped by event
  const participantFields = useMemo(
    () => (event ? getParticipantFields(event, filters.tickets, filters.products) : []),
    [event, filters.tickets, filters.products],
  );

  const getDefaultShowingFilters = (filters: RegistrationFilterParams) => ({
    age: !!filters.min_age || !!filters.max_age,
    locales: filters.locales.length > 0,
    genders: filters.genders.length > 0,
    mail_opt_ins: filters.mail_opt_ins?.length > 0,
    tickets: filters.ticket_categories?.length > 0 || filters.tickets.length > 0 || filters.time_slots.length
      || filters.promotions.length > 0,
    products: filters.products?.length > 0 || filters.product_variants.length > 0 || filters.promotions.length > 0,
    statuses: filters.statuses.length > 0,
    assignment_statuses: filters.assignment_statuses.length > 0,
    payment_statuses: filters.payment_statuses.length > 0,
    participant_fields: filters.participant_fields.length > 0
      || filters.participant_field_choices.length > 0,
    ticket_number_sequences: filters.ticket_number_sequences.length > 0
      || filters.has_ticket_number !== null,
    team: filters.has_team !== null,
    invitations: filters.invitations.length > 0,
    coupons: filters.coupons.length > 0,
    lotteries: filters.lotteries.length > 0,
    confirmed_lotteries: filters.confirmed_lotteries.length > 0,
    rejected_lotteries: filters.rejected_lotteries.length > 0,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    onChange((filters) => ({
      ...defaultFilters, // Reset all filters except search.
      search: filters.search,
      event: event?.id,
    }));
  };

  /**
   * Handles changes to the min age input by storing the value in the filters.
   */
  const handleMinAgeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      min_age: value ? parseInt(value, 10) : null,
    }));
  };

  /**
   * Handles changes to the min age input by storing the value in the filters.
   */
  const handleMaxAgeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      max_age: value ? parseInt(value, 10) : null,
    }));
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: event.target.value || null }));
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: null }));

    searchRef.current.value = '';
  };

  // Only the keys of RegistrationFilterParams that have an array as value
  type RegistrationFilterListType = {
    [K in keyof RegistrationFilterParams]-?: RegistrationFilterParams[K] extends Array<any> ? K : never
  }[keyof RegistrationFilterParams];

  /**
   * Toggles a filter in a list: if it's not in the list, it's added, otherwise it's removed.
   */
  const toggleListFilter = (name: RegistrationFilterListType, id: string) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      [name]: toggleInList([...filters[name] || []], id),
    }));
  };

  /**
   * Toggles a gender filter.
   */
  const toggleGenderFilter = (gender?: Gender) => {
    toggleListFilter('genders', gender);
  };

  /**
   * Toggles a locale filter.
   */
  const toggleLocaleFilter = (locale?: Locale) => {
    toggleListFilter('locales', locale);
  };

  /**
   * Toggles a ticket category filter.
   */
  const toggleTicketCategoryFilter = (ticketCategoryId: string) => {
    toggleListFilter('ticket_categories', ticketCategoryId);
  };

  /**
   * Toggles a ticket filter.
   */
  const toggleTicketFilter = (ticketId: string) => {
    if (event.ticket_categories.length === 1) {
      const newTicketIds = toggleInList([...filters.tickets || []], ticketId);

      // If there is only one ticket category, the ticket category filter is not shown,
      // so it must be added/removed automatically.
      onChange((filters) => ({
        ...filters,
        ticket_categories: newTicketIds.length > 0 ? [event.ticket_categories[0].id] : [],
        tickets: newTicketIds,
      }));
    } else {
      toggleListFilter('tickets', ticketId);
    }
  };

  /**
   * Toggles a time slot filter.
   */
  const toggleTimeSlotFilter = (timeSlotId: string) => {
    toggleListFilter('time_slots', timeSlotId);
  };

  /**
   * Toggles a product filter.
   */
  const toggleProductFilter = (productId: string) => {
    toggleListFilter('products', productId);
  };

  /**
   * Toggles an invitation code filter.
   */
  const toggleInvitationFilter = (invitationId: string) => {
    toggleListFilter('invitations', invitationId);
  };

  /**
   * Toggles a coupon filter.
   */
  const toggleCouponFilter = (couponId: string) => {
    toggleListFilter('coupons', couponId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleLotteryFilter = (lotteryId: string) => {
    toggleListFilter('lotteries', lotteryId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleConfirmedLotteryFilter = (lotteryId: string) => {
    toggleListFilter('confirmed_lotteries', lotteryId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleRejectedLotteryFilter = (lotteryId: string) => {
    toggleListFilter('rejected_lotteries', lotteryId);
  };

  /**
   * Toggles a product variant filter.
   */
  const toggleProductVariantFilter = (productVariantId: string) => {
    toggleListFilter('product_variants', productVariantId);
  };

  /**
   * Toggles a promotion filter.
   */
  const togglePromotionFilter = (promotionId: string) => {
    toggleListFilter('promotions', promotionId);
  };

  /**
   * Toggles a purchase status filter.
   */
  const toggleStatusFilter = (status: PurchaseStatus) => {
    toggleListFilter('statuses', status);
  };

  /**
   * Toggles an assignment status filter.
   */
  const toggleAssignmentStatusFilter = (status: AssignmentStatus) => {
    toggleListFilter('assignment_statuses', status);
  };

  /**
   * Toggles a payment status filter.
   */
  const togglePaymentStatusFilter = (status: PurchasePaymentStatus) => {
    toggleListFilter('payment_statuses', status);
  };

  /**
   * Toggles a participant field filter.
   */
  const toggleParticipantFieldFilter = (participantFieldId: string) => {
    onChange((filters) => {
      const index = filters.participant_fields.findIndex(({ id }) => id === participantFieldId);

      const participantFields = index === -1
        ? [...filters.participant_fields, { id: participantFieldId }]
        : filters.participant_fields.filter(({ id }) => id !== participantFieldId);

      return {
        ...defaultFilters,
        ...filters,
        participant_fields: participantFields,
      };
    });
  };

  /**
   * Updates the filter object for a participant field.
   */
  const setParticipantFieldFilters = (
    fieldId: string,
    update: (fieldFilters: ParticipantFieldFilters) => ParticipantFieldFilters,
  ) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      participant_fields: filters.participant_fields.map((field) => (field.id === fieldId ? {
        ...field,
        filters: update(field.filters),
      } : field)),
    }));
  };

  /**
   * Toggles a participant field choice filter.
   */
  const toggleParticipantFieldChoiceFilter = (participantFieldChoiceId: string) => {
    toggleListFilter('participant_field_choices', participantFieldChoiceId);
  };

  /**
   * Toggles a ticket number sequence filter.
   */
  const toggleTicketNumberSequenceFilter = (sequenceId: string) => {
    toggleListFilter('ticket_number_sequences', sequenceId);
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, the UI state and search input
   */
  const resetFilters = (filters?: RegistrationFilterParams) => {
    const newFilters = { ...defaultFilters, ...filters };

    onChange(() => newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters.search || '';
    }
  };

  // Keep the filtered time slots synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleTickets = event?.ticket_categories.reduce(
        (tickets, ticketCategory) => [...tickets, ...ticketCategory.tickets],
        [],
      ) || [];
      const possibleTicketIds = map(possibleTickets, 'id');
      const newTicketIds = intersection(filters.tickets, possibleTicketIds);

      const possibleTimeSlotIds = map(flatten(map(possibleTickets.filter((ticket) => (
        filters.tickets.includes(ticket.id)
      )), 'time_slots')), 'id');

      const newTimeSlotIds = intersection(filters.time_slots, possibleTimeSlotIds);

      if (newTicketIds.join() !== filters.tickets.join()
        || newTimeSlotIds.join() !== filters.time_slots.join()) {
        onChange((filters) => ({
          ...filters,
          tickets: newTicketIds,
          time_slots: newTimeSlotIds,
        }));
      }
    }
  }, [event, filters.event, filters.tickets, filters.time_slots]);

  // Keep the filtered product variants synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleProducts = event?.products || [];
      const possibleProductIds = map(possibleProducts, 'id');
      const newProductIds = intersection(filters.products, possibleProductIds);

      const possibleProductVariantIds = map(flatten(map(possibleProducts.filter((product) => (
        filters.products.includes(product.id)
      )), 'product_variants')), 'id');

      const newProductVariantIds = intersection(filters.product_variants, possibleProductVariantIds);

      if (newProductIds.join() !== filters.products.join()
        || newProductVariantIds.join() !== filters.product_variants.join()) {
        onChange((filters) => ({
          ...filters,
          products: newProductIds,
          product_variants: newProductVariantIds,
        }));
      }
    }
  }, [event, filters.event, filters.products, filters.product_variants]);

  // Keep the filtered promotions synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleTicketCategories = event?.ticket_categories || [];
      const possibleTicketCategoryIds = map(possibleTicketCategories, 'id');
      const newTicketCategoryIds = intersection(filters.ticket_categories, possibleTicketCategoryIds);

      const possibleTickets = event?.ticket_categories.reduce(
        (tickets, ticketCategory) => (!newTicketCategoryIds.includes(ticketCategory.id)
          ? tickets
          : [...tickets, ...ticketCategory.tickets]),
        [],
      ) || [];
      const possibleTicketIds = map(possibleTickets, 'id');
      const newTicketIds = intersection(filters.tickets, possibleTicketIds);

      const possibleProducts = event?.products || [];
      const possibleProductIds = map(possibleProducts, 'id');
      const newProductIds = intersection(filters.products, possibleProductIds);

      const possiblePromotionIds = [
        ...map(flatten(map(possibleTickets.filter((ticket) => (
          filters.tickets.includes(ticket.id)
        )), 'promotions')), 'id'),
        ...map(flatten(map(possibleProducts.filter((product) => (
          filters.products.includes(product.id)
        )), 'promotions')), 'id'),
      ];

      const newPromotionIds = intersection(filters.promotions, possiblePromotionIds);

      if (newTicketCategoryIds.join() !== filters.ticket_categories.join()
        || newTicketIds.join() !== filters.tickets.join()
        || newProductIds.join() !== filters.products.join()
        || newPromotionIds.join() !== filters.promotions.join()) {
        onChange((filters) => ({
          ...filters,
          ticket_categories: newTicketCategoryIds,
          tickets: newTicketIds,
          products: newProductIds,
          promotions: newPromotionIds,
        }));
      }
    }
  }, [
    event, filters.event, filters.ticket_categories, filters.tickets,
    filters.products, filters.promotions, onChange,
  ]);

  // Keep the filtered participant fields and choices synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleFieldIds = map(participantFields, 'id');
      const selectedParticipantFieldIds = filters.participant_fields.map(({ id }) => id);
      const newFieldIds = intersection(selectedParticipantFieldIds, possibleFieldIds);

      const possibleChoiceIds = map(flatten(map(participantFields.filter((field) => (
        selectedParticipantFieldIds.includes(field.id)
      )), 'choices')), 'id');

      const newChoiceIds = intersection(filters.participant_field_choices, possibleChoiceIds);

      if (newFieldIds.join() !== selectedParticipantFieldIds.join()
        || newChoiceIds.join() !== filters.participant_field_choices.join()) {
        onChange((filters) => ({
          ...filters,
          participant_fields: newFieldIds.map((id) => ({ id })),
          participant_field_choices: newChoiceIds,
        }));
      }
    }
  }, [event, filters.event, filters.participant_fields, filters.participant_field_choices, participantFields]);

  const invitations = event?.invitations || [];
  const coupons = event?.coupons || [];
  const lotteries = event?.lotteries || [];

  const selectedActiveStatus = filters.statuses.includes(PurchaseStatus.complete)
    || filters.statuses.includes(PurchaseStatus.incomplete);

  const toggleActiveFilter = () => {
    if (selectedActiveStatus) {
      onChange((filters) => ({
        ...filters,
        statuses: difference(filters.statuses, [
          PurchaseStatus.complete, PurchaseStatus.incomplete,
        ]),
      }));
    } else {
      onChange((filters) => ({
        ...filters,
        statuses: [
          ...filters.statuses,
          PurchaseStatus.complete,
          PurchaseStatus.incomplete,
        ].sort(),
      }));
    }
  };

  const toggleHasTeamFilter = (value: boolean | null) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      has_team: value,
    }));
  };
  const toggleHasTicketNumberFilter = () => {
    // Toggle between 'false' and 'null'.
    // Setting 'has_ticket_number' to 'true' is not possible in the interface.
    // This can be achieved by selecting ticket number sequences instead.
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      has_ticket_number: filters.has_ticket_number === false ? null : false,
    }));
  };

  return (
    <UI.FormGrid sc={{ gutter: 0.75 }}>
      {enabled('event') && (
        <UI.Div>
          <EventPicker
            eventId={filters.event}
            onChange={selectEvent}
            renderActive={(event) => (
              <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                {event?.title || t('all_events')}
              </UI.Span>
            )}
          />
        </UI.Div>
      )}

      {enabled('search') && (
        <UI.InputGroup>
          <UI.InputWrapper>
            <UI.Span>
              <UI.Icon>
                <Search />
              </UI.Icon>
            </UI.Span>
            <UI.DebouncedInput
              type="text"
              value={filters.search}
              onChange={handleSearch}
              placeholder={t('search_registrations')}
              ref={searchRef}
              sc={{ px: 5 }}
              disabled={readOnly}
            />
            {filters.search && (
              <UI.Span>
                <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                </UI.A>
              </UI.Span>
            )}
          </UI.InputWrapper>
        </UI.InputGroup>
      )}

      {event && (
        <>
          {event.ticket_categories.length > 0 && enabled('tickets') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('tickets')}
                  role="button"
                  sc={{ mb: showingFilters.tickets ? 2 : 0 }}
                >
                  {t('tickets')}
                  {' '}
                  {!showingFilters.tickets && filters.tickets.length > 0 && (
                    <UI.Badge>{filters.tickets.length}</UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.tickets && <ChevronUp />}
                    {!showingFilters.tickets && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>
                {showingFilters.tickets && event.ticket_categories.map((ticketCategory) => (
                  <UI.Div key={ticketCategory.id}>
                    {event.ticket_categories.length > 1 && (
                      <UI.Checkbox
                        checked={filters.ticket_categories.includes(ticketCategory.id)}
                        onChange={() => toggleTicketCategoryFilter(ticketCategory.id)}
                        disabled={readOnly}
                      >
                        {ticketCategory.title}
                      </UI.Checkbox>
                    )}
                    {(event.ticket_categories.length === 1
                      || filters.ticket_categories.includes(ticketCategory.id)) && (
                      <UI.Div sc={{ pl: event.ticket_categories.length > 1 ? 4 : 0 }}>
                        {ticketCategory.tickets.map((ticket) => (
                          <UI.Div key={ticket.id}>
                            <UI.Checkbox
                              checked={filters.tickets.includes(ticket.id)}
                              onChange={() => toggleTicketFilter(ticket.id)}
                              disabled={readOnly}
                            >
                              {ticket.title}
                            </UI.Checkbox>
                            {filters.tickets.includes(ticket.id)
                              && (ticket.promotions.length > 1 || ticket.time_slots.length > 0)
                              && (
                                <UI.Div sc={{ pl: 4 }}>
                                  {ticket.promotions.length > 1 && ticket.promotions.map((promotion) => (
                                    <UI.Checkbox
                                      checked={filters.promotions.includes(promotion.id)}
                                      onChange={() => togglePromotionFilter(promotion.id)}
                                      disabled={readOnly}
                                      key={promotion.id}
                                    >
                                      <UI.Icon sc={{ muted: true }}>
                                        <Tag />
                                      </UI.Icon>
                                      {' '}
                                      {promotion.title || t('standard')}
                                    </UI.Checkbox>
                                  ))}
                                  {ticket.time_slots.length > 0 && ticket.time_slots.map((timeSlot) => (
                                    <UI.Checkbox
                                      checked={filters.time_slots.includes(timeSlot.id)}
                                      onChange={() => toggleTimeSlotFilter(timeSlot.id)}
                                      disabled={readOnly}
                                      key={timeSlot.id}
                                    >
                                      <UI.Icon sc={{ muted: true }}>
                                        <Clock />
                                      </UI.Icon>
                                      {' '}
                                      <UI.Delimit>
                                        {formatTimeSlot(timeSlot)}
                                        {timeSlot.title}
                                      </UI.Delimit>
                                    </UI.Checkbox>
                                  ))}
                                </UI.Div>
                              )}
                          </UI.Div>
                        ))}
                      </UI.Div>
                    )}
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {event.ticket_number_sequences.length > 0 && enabled('ticket_number_sequences') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('ticket_number_sequences')}
                  role="button"
                  sc={{ mb: showingFilters.ticket_number_sequences ? 2 : 0 }}
                >
                  {t('ticket_numbers')}
                  {' '}
                  {!showingFilters.ticket_number_sequences
                      && (filters.ticket_number_sequences.length > 0 || filters.has_ticket_number !== null) && (
                      <UI.Badge>
                        {(filters.ticket_number_sequences.length || 0) + (filters.has_ticket_number !== null ? 1 : 0)}
                      </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.ticket_number_sequences && <ChevronUp />}
                    {!showingFilters.ticket_number_sequences && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>
                {showingFilters.ticket_number_sequences && (
                  <>
                    {event.ticket_number_sequences.map((sequence) => (
                      <UI.Div key={sequence.id}>
                        <UI.Checkbox
                          checked={filters.ticket_number_sequences.includes(sequence.id)}
                          onChange={() => toggleTicketNumberSequenceFilter(sequence.id)}
                          disabled={readOnly}
                        >
                          {sequence.title}
                        </UI.Checkbox>
                      </UI.Div>
                    ))}
                    <UI.Checkbox
                      checked={filters.has_ticket_number === false}
                      onChange={() => toggleHasTicketNumberFilter()}
                      disabled={readOnly}
                    >
                      {t('without_ticket_number')}
                    </UI.Checkbox>
                  </>
                )}
              </UI.InputGroup>
            </>
          )}

          {products.length > 0 && enabled('products') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('products')}
                  role="button"
                  sc={{ mb: showingFilters.products ? 2 : 0 }}
                >
                  {t('products')}
                  {' '}
                  {!showingFilters.products && filters.products.length > 0 && (
                  <UI.Badge>{filters.products.length}</UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.products && <ChevronUp />}
                    {!showingFilters.products && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>
                {showingFilters.products && products.map((product) => (
                  <UI.Div key={product.id}>
                    <UI.Checkbox
                      checked={filters.products.includes(product.id)}
                      onChange={() => toggleProductFilter(product.id)}
                      disabled={readOnly}
                    >
                      {product.title}
                    </UI.Checkbox>
                    {filters.products.includes(product.id)
                      && (product.promotions.length > 1 || product.product_variants.length > 0)
                      && (
                        <UI.Div sc={{ pl: 4 }}>
                          {product.promotions.length > 1 && product.promotions.map((promotion) => (
                            <UI.Checkbox
                              checked={filters.promotions.includes(promotion.id)}
                              onChange={() => togglePromotionFilter(promotion.id)}
                              disabled={readOnly}
                              key={promotion.id}
                            >
                              <UI.Icon sc={{ muted: true }}>
                                <Tag />
                              </UI.Icon>
                              {' '}
                              {promotion.title || t('standard')}
                            </UI.Checkbox>
                          ))}
                          {product.product_variants.length > 0
                            && product.product_variants.map((productVariant) => (
                              <UI.Checkbox
                                checked={filters.product_variants.includes(productVariant.id)}
                                onChange={() => toggleProductVariantFilter(productVariant.id)}
                                disabled={readOnly}
                                key={productVariant.id}
                              >
                                <SellableLabel type="product">
                                  {productVariant.title}
                                </SellableLabel>
                              </UI.Checkbox>
                            ))}
                        </UI.Div>
                      )}
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {participantFields.length > 0 && enabled('participant_fields') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('participant_fields')}
                  role="button"
                  sc={{ mb: showingFilters.participant_fields ? 2 : 0 }}
                >
                  {t('custom_fields')}
                  {' '}
                  {!showingFilters.participant_fields
                    && (filters.participant_field_choices.length > 0 || filters.participant_fields.length > 0) && (
                    <UI.Badge>
                      {(filters.participant_field_choices.length || 0) + (filters.participant_fields.length || 0)}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.participant_fields && <ChevronUp />}
                    {!showingFilters.participant_fields && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.participant_fields && (
                  <UI.Div>
                    {participantFields.map((field) => (
                      <UI.Div key={field.id}>
                        <UI.Checkbox
                          checked={filters.participant_fields.map(({ id }) => id).includes(field.id)}
                          onChange={() => toggleParticipantFieldFilter(field.id)}
                          disabled={readOnly}
                        >
                          <UI.Span sc={{ muted: !field.enabled }}>
                            {field.title}
                          </UI.Span>
                        </UI.Checkbox>
                        {filters.participant_fields.map(({ id }) => id).includes(field.id) && (
                          <UI.Div sc={{ pl: 4 }}>
                            <ParticipantFieldFiltersForm
                              field={field}
                              filters={
                                filters.participant_fields.filter(
                                  ({ id }) => id === field.id,
                                )[0].filters
                              }
                              onChange={(update) => setParticipantFieldFilters(field.id, update)}
                            />
                            {field.choices.map((choice) => (
                              <UI.Checkbox
                                checked={filters.participant_field_choices.includes(choice.id)}
                                onChange={() => toggleParticipantFieldChoiceFilter(choice.id)}
                                disabled={readOnly}
                                key={choice.id}
                              >
                                <UI.Span sc={{ muted: !choice.enabled }}>
                                  {choice.title}
                                </UI.Span>
                              </UI.Checkbox>
                            ))}
                          </UI.Div>
                        )}
                      </UI.Div>
                    ))}
                  </UI.Div>
                )}
              </UI.InputGroup>
            </>
          )}

          {invitations.length > 0 && enabled('invitations') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('invitations')}
                  role="button"
                  sc={{ mb: showingFilters.invitations ? 2 : 0 }}
                >
                  {t('invitation_codes')}
                  {' '}
                  {!showingFilters.invitations && filters.invitations.length > 0 && (
                    <UI.Badge>
                      {filters.invitations.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.invitations && <ChevronUp />}
                    {!showingFilters.invitations && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.invitations && invitations.map((invitation) => (
                  <UI.Div key={invitation.id}>
                    <UI.Checkbox
                      checked={filters.invitations.includes(invitation.id)}
                      onChange={() => toggleInvitationFilter(invitation.id)}
                      disabled={readOnly}
                    >
                      <UI.Label>
                        <UI.Strong>{invitation.prefix}</UI.Strong>
                      </UI.Label>
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {coupons.length > 0 && enabled('coupons') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('coupons')}
                  role="button"
                  sc={{ mb: showingFilters.coupons ? 2 : 0 }}
                >
                  {t('coupons')}
                  {' '}
                  {!showingFilters.coupons && filters.coupons.length > 0 && (
                    <UI.Badge>
                      {filters.coupons.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.coupons && <ChevronUp />}
                    {!showingFilters.coupons && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.coupons && coupons.map((coupon) => (
                  <UI.Div key={coupon.id}>
                    <UI.Checkbox
                      checked={filters.coupons.includes(coupon.id)}
                      onChange={() => toggleCouponFilter(coupon.id)}
                      disabled={readOnly}
                    >
                      <UI.Label>
                        <UI.Strong>{coupon.prefix}</UI.Strong>
                      </UI.Label>
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {lotteries.length > 0 && enabled('lotteries') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('lotteries')}
                  role="button"
                  sc={{ mb: showingFilters.lotteries ? 2 : 0 }}
                >
                  {t('selected_by_lottery')}
                  {' '}
                  {!showingFilters.lotteries && filters.lotteries.length > 0 && (
                    <UI.Badge>
                      {filters.lotteries.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.lotteries && <ChevronUp />}
                    {!showingFilters.lotteries && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.lotteries && lotteries.map((lottery) => (
                  <UI.Div key={lottery.id}>
                    <UI.Checkbox
                      checked={filters.lotteries.includes(lottery.id)}
                      onChange={() => toggleLotteryFilter(lottery.id)}
                      disabled={readOnly}
                    >
                      {lottery.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {lotteries.length > 0 && enabled('confirmed_lotteries') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('confirmed_lotteries')}
                  role="button"
                  sc={{ mb: showingFilters.confirmed_lotteries ? 2 : 0 }}
                >
                  {t('originating_from_lottery')}
                  {' '}
                  {!showingFilters.confirmed_lotteries && filters.confirmed_lotteries.length > 0 && (
                    <UI.Badge>
                      {filters.confirmed_lotteries.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.confirmed_lotteries && <ChevronUp />}
                    {!showingFilters.confirmed_lotteries && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.confirmed_lotteries && lotteries.map((lottery) => (
                  <UI.Div key={lottery.id}>
                    <UI.Checkbox
                      checked={filters.confirmed_lotteries.includes(lottery.id)}
                      onChange={() => toggleConfirmedLotteryFilter(lottery.id)}
                      disabled={readOnly}
                    >
                      {lottery.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {lotteries.length > 0 && enabled('rejected_lotteries') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('rejected_lotteries')}
                  role="button"
                  sc={{ mb: showingFilters.rejected_lotteries ? 2 : 0 }}
                >
                  {t('rejected_by_lottery')}
                  {' '}
                  {!showingFilters.rejected_lotteries && filters.rejected_lotteries.length > 0 && (
                    <UI.Badge>
                      {filters.rejected_lotteries.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.rejected_lotteries && <ChevronUp />}
                    {!showingFilters.rejected_lotteries && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.rejected_lotteries && lotteries.map((lottery) => (
                  <UI.Div key={lottery.id}>
                    <UI.Checkbox
                      checked={filters.rejected_lotteries.includes(lottery.id)}
                      onChange={() => toggleRejectedLotteryFilter(lottery.id)}
                      disabled={readOnly}
                    >
                      {lottery.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}
        </>
      )}

      {enabled('team') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('team')}
              role="button"
              sc={{ mb: showingFilters.team ? 2 : 0 }}
            >
              {t('team')}
              {' '}
              {!showingFilters.team && filters.has_team !== null && (
              <UI.Badge>
                1
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.team && <ChevronUp />}
                {!showingFilters.team && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.team && (
              <UI.Div>
                <UI.Radio
                  checked={filters.has_team === null}
                  onChange={() => toggleHasTeamFilter(null)}
                  disabled={readOnly}
                >
                  {t('all')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.has_team === true}
                  onChange={() => toggleHasTeamFilter(true)}
                  disabled={readOnly}
                >
                  {t('with_team')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.has_team === false}
                  onChange={() => toggleHasTeamFilter(false)}
                  disabled={readOnly}
                >
                  {t('without_team')}
                </UI.Radio>
              </UI.Div>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('statuses') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('statuses')}
              role="button"
              sc={{ mb: showingFilters.statuses ? 2 : 0 }}
            >
              {t('status')}
              {' '}
              {!showingFilters.statuses && filters.statuses.length > 0 && (
              <UI.Badge>
                {filters.statuses.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.statuses && <ChevronUp />}
                {!showingFilters.statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={selectedActiveStatus}
                  onChange={() => toggleActiveFilter()}
                  disabled={readOnly}
                >
                  {t('active')}
                </UI.Checkbox>
                {selectedActiveStatus && (
                  <UI.Div sc={{ pl: 4 }}>
                    <UI.Checkbox
                      checked={filters.statuses.includes(PurchaseStatus.complete)}
                      onChange={() => toggleStatusFilter(PurchaseStatus.complete)}
                      disabled={readOnly}
                    >
                      {t('complete')}
                    </UI.Checkbox>
                    <UI.Checkbox
                      checked={filters.statuses.includes(PurchaseStatus.incomplete)}
                      onChange={() => toggleStatusFilter(PurchaseStatus.incomplete)}
                      disabled={readOnly}
                    >
                      {t('incomplete')}
                    </UI.Checkbox>
                  </UI.Div>
                )}
                <UI.Div>
                  <UI.Checkbox
                    checked={filters.statuses.includes(PurchaseStatus.waitlist)}
                    onChange={() => toggleStatusFilter(PurchaseStatus.waitlist)}
                    disabled={readOnly}
                  >
                    {t('waitlist')}
                  </UI.Checkbox>
                </UI.Div>
                <UI.Div>
                  <UI.Checkbox
                    checked={filters.statuses.includes(PurchaseStatus.inactive)}
                    onChange={() => toggleStatusFilter(PurchaseStatus.inactive)}
                    disabled={readOnly}
                  >
                    {t('cancelled')}
                  </UI.Checkbox>
                </UI.Div>
              </UI.Div>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('assignment_statuses') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('assignment_statuses')}
              role="button"
              sc={{ mb: showingFilters.assignment_statuses ? 2 : 0 }}
            >
              {t('assignment_status')}
              {' '}
              {!showingFilters.assignment_statuses && filters.assignment_statuses.length > 0 && (
              <UI.Badge>
                {filters.assignment_statuses.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.assignment_statuses && <ChevronUp />}
                {!showingFilters.assignment_statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.assignment_statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={filters.assignment_statuses.includes(AssignmentStatus.assigned)}
                  onChange={() => toggleAssignmentStatusFilter(AssignmentStatus.assigned)}
                  disabled={readOnly}
                >
                  {t('assigned')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.assignment_statuses.includes(AssignmentStatus.invited)}
                  onChange={() => toggleAssignmentStatusFilter(AssignmentStatus.invited)}
                  disabled={readOnly}
                >
                  {t('invited')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.assignment_statuses.includes(AssignmentStatus.unassigned)}
                  onChange={() => toggleAssignmentStatusFilter(AssignmentStatus.unassigned)}
                  disabled={readOnly}
                >
                  {t('unassigned')}
                </UI.Checkbox>
              </UI.Div>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('payment_statuses') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('payment_statuses')}
              role="button"
              sc={{ mb: showingFilters.payment_statuses ? 2 : 0 }}
            >
              {t('payment_status')}
              {' '}
              {!showingFilters.payment_statuses && filters.payment_statuses.length > 0 && (
              <UI.Badge>
                {filters.payment_statuses.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.payment_statuses && <ChevronUp />}
                {!showingFilters.payment_statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.payment_statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.paid)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.paid)}
                  disabled={readOnly}
                >
                  {t('paid')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.open)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.open)}
                  disabled={readOnly}
                >
                  {t('open')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.refunded)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.refunded)}
                  disabled={readOnly}
                >
                  {t('refunded')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.has_balance)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.has_balance)}
                  disabled={readOnly}
                >
                  {t('credit')}
                </UI.Checkbox>
              </UI.Div>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('age') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('age')}
              role="button"
              sc={{ mb: showingFilters.age ? 2 : 0 }}
            >
              {t('age')}
              {' '}
              {!showingFilters.age && (filters.min_age || filters.max_age) && (
              <UI.Badge>
                {filters.min_age || 0}
                {filters.max_age && <>&ndash;</>}
                {!filters.max_age && '+'}
                {filters.max_age}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.age && <ChevronUp />}
                {!showingFilters.age && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.age && (
              <UI.FormGrid sc={{ columns: '1fr 1fr' }} style={{ maxWidth: 200 }}>
                <UI.Div>
                  <UI.InputLabel htmlFor="MinAge">
                    {t('min')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.min_age}
                    onChange={handleMinAgeChange}
                    maxLength={3}
                    inputMode="numeric"
                    match={/[\d]/g}
                    disabled={readOnly}
                    id="MinAge"
                  />
                </UI.Div>
                <UI.Div>
                  <UI.InputLabel htmlFor="MaxAge">
                    {t('max')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.max_age}
                    onChange={handleMaxAgeChange}
                    maxLength={3}
                    inputMode="numeric"
                    match={/[\d]/g}
                    disabled={readOnly}
                    id="MaxAge"
                  />
                </UI.Div>
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('gender') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('genders')}
              role="button"
              sc={{ mb: showingFilters.genders ? 2 : 0 }}
            >
              {t('gender')}
              {' '}
              {!showingFilters.genders && filters.genders.length > 0 && (
              <UI.Badge>
                {filters.genders.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.genders && <ChevronUp />}
                {!showingFilters.genders && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.genders && (
              <>
                <UI.Checkbox
                  checked={filters.genders.includes(Gender.male)}
                  onChange={() => toggleGenderFilter(Gender.male)}
                  disabled={readOnly}
                >
                  {t('common:genders.male')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.genders.includes(Gender.female)}
                  onChange={() => toggleGenderFilter(Gender.female)}
                  disabled={readOnly}
                >
                  {t('common:genders.female')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.genders.includes(Gender.neutral)}
                  onChange={() => toggleGenderFilter(Gender.neutral)}
                  disabled={readOnly}
                >
                  <UI.Span sc={{ color: !project.genders.includes(Gender.neutral) ? 'gray.400' : undefined }}>
                    {t('common:genders.neutral')}
                  </UI.Span>
                  {!project.genders.includes(Gender.neutral) && (
                    <ProtectedSection permissions={[Permission.admin]}>
                      {' '}
                      <UI.Link
                        to={`/${project.id}/project/features`}
                        sc={{ secondary: true, color: 'gray.400' }}
                      >
                        <UI.Icon>
                          <Settings />
                        </UI.Icon>
                      </UI.Link>
                    </ProtectedSection>
                  )}
                </UI.Checkbox>
              </>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('locales') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('locales')}
              role="button"
              sc={{ mb: showingFilters.locales ? 2 : 0 }}
            >
              {t('preferred_language')}
              {' '}
              {!showingFilters.locales && filters.locales.length > 0 && (
              <UI.Badge>
                {filters.locales.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.locales && <ChevronUp />}
                {!showingFilters.locales && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.locales && config.locales.map((locale) => (
              <UI.Checkbox
                key={`locale_${locale}`}
                checked={filters.locales.includes(locale)}
                onChange={() => toggleLocaleFilter(locale)}
                disabled={readOnly}
              >
                {t(`common:locales.${locale}`)}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        </>
      )}

      {mailPermissions.length > 0 && enabled('mail_opt_ins') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('mail_opt_ins')}
              role="button"
              sc={{ mb: showingFilters.mail_opt_ins ? 2 : 0 }}
            >
              {t('mail_opt_ins')}
              {' '}
              {!showingFilters.mail_opt_ins && filters.mail_opt_ins?.length > 0 && (
                <UI.Badge>{filters.mail_opt_ins.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.mail_opt_ins && <ChevronUp />}
                {!showingFilters.mail_opt_ins && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.mail_opt_ins && mailPermissions.map((mailPermission) => (
              <UI.Checkbox
                onChange={() => toggleListFilter('mail_opt_ins', mailPermission.id)}
                checked={filters.mail_opt_ins.includes(mailPermission.id)}
                disabled={readOnly}
                key={mailPermission.id}
              >
                {mailPermission.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        </>
      )}

      {showTotals && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />

          <UI.Div sc={{ loading: loadingStats }}>
            <UI.Div sc={{ mb: 2 }}>
              <UI.Delimit>
                <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
                  <UI.Icon sc={{ mr: 1 }}>
                    <BarChart2 />
                  </UI.Icon>
                  {t('in_this_selection')}
                </UI.Span>
                {showViewParticipantsButton && (
                  <UI.A href={`/b/${project.id}/participants?${encodeUrl(filters)}`} target="_blank">
                    {t('view')}
                  </UI.A>
                )}
              </UI.Delimit>
            </UI.Div>
            <UI.FlexContainer>
              <UI.Div sc={{ mr: 3 }} style={{ minWidth: 55 }}>
                <UI.H4>{formatNumber(stats?.tickets || 0)}</UI.H4>
                <UI.Small>{t('registrations', { count: stats?.tickets || 0 })}</UI.Small>
              </UI.Div>
            </UI.FlexContainer>
          </UI.Div>
        </>
      )}

      {showResetButton && hasFilters && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.Div>
            <UI.Button onClick={() => resetFilters()}>
              {t('reset_filters')}
            </UI.Button>
          </UI.Div>
        </>
      )}
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: block;
    line-height: 21px;
  `}
`;

export default RegistrationFilters;
